/*
 * attachments.ts — Attachment list rendering, file dropzone, and local file management.
 */

/* global document, Office */

import {
  collectedEmails,
  selectedFiles,
  localFiles,
  addLocalFiles,
  removeLocalFile,
  getEmailFileId,
  getAttachmentFileId,
  isFileNameUploaded,
  setFileNameUploaded,
  isFileChecked,
  hasDegradedEmailSelection,
  requestSelectionRefresh,
} from "./email-context";
import {  DocType  } from "./bc-api";
import { DocumentTypes } from "./document-types";
import { getOrCreateAttachmentDetails, setDocType, setComment, setAllowSynchToPortal } from "./attachment-details";
import { updateMapUploadButtonState, clearMapUploadStatus, getSingleAutoSelectedOrderNo, setStatusBanner } from "./orders";
import { isFileAlreadyLinked } from "./bc-service";
import { getBcRequestContext } from "./taskpane-auth";
import type { BcRequestContext } from "../shared/auth-state";
import { escapeHtml, formatSize, getFileIcon, normalizeFileNameForCompare, getExtension, normalizeEmlFileName, getErrorMessage } from "./helpers";
import type { AttachmentDetailsExt, DragAndDropEventArgsExt, DroppedItemDetailsExt } from "../shared/office-compat";

// ─── Types ──────────────────────────────────

export interface FileEntry {
  id: string;
  name: string;
  size: number | null;
  isLocal: boolean;
  sourceEmailKey?: string;
}

let categoryMenuInitialized = false;

// ─── Helpers ────────────────────────────────

function closeAllCategoryMenus(): void {
    document
        .querySelectorAll(".file-category")
        .forEach(item =>
            item.classList.remove("file-category--open"));
}

export function initializeCategoryMenu(): void {
    if (categoryMenuInitialized) return;

    categoryMenuInitialized = true;
    document.addEventListener("click", closeAllCategoryMenus);
    window.addEventListener(
        "scroll",
        (event) => {
            const target = event.target;
            if (target instanceof Element && target.closest(".file-category-menu")) return;
            closeAllCategoryMenus();
        },
        true,
    );
    window.addEventListener("resize", closeAllCategoryMenus);
}

function positionCategoryMenu(wrapper: HTMLElement, button: HTMLElement): void {
    const menu = wrapper.querySelector<HTMLElement>(".file-category-menu");
    if (!menu) return;

    const GAP = 2;
    // ~20 rows at the current row height (~25px: 8px padding + line-height, see
    // .file-category-option in taskpane.css). Purely a preferred ceiling -- the menu is
    // position: fixed and the clamping below (Math.min against spaceBelow/spaceAbove)
    // already keeps it from ever exceeding actual viewport room, so raising this can't
    // make the task pane itself grow a scrollbar; it only lets the menu grow taller when
    // there's space for it.
    const PREFERRED_MAX_HEIGHT = 500; // the cap this applies as an inline style on .file-category-menu below
    const buttonRect = button.getBoundingClientRect();
    const spaceBelow = window.innerHeight - buttonRect.bottom - GAP;
    const spaceAbove = buttonRect.top - GAP;
    const openUpward = spaceBelow < Math.min(PREFERRED_MAX_HEIGHT, menu.scrollHeight) && spaceAbove > spaceBelow;

    menu.style.left = `${buttonRect.left}px`;
    menu.style.width = `${buttonRect.width}px`;
    menu.style.maxHeight = `${Math.max(60, Math.min(PREFERRED_MAX_HEIGHT, openUpward ? spaceAbove : spaceBelow))}px`;

    if (openUpward) {
        menu.style.top = "auto";
        menu.style.bottom = `${window.innerHeight - buttonRect.top + GAP}px`;
    } else {
        menu.style.bottom = "auto";
        menu.style.top = `${buttonRect.bottom + GAP}px`;
    }
}

/**
 * Shows/hides each .file-category-option under wrapper by whether its label (or its
 * underlying code) contains query, case-insensitively -- the blank "Document Type" (clear
 * selection) option always stays visible, since it isn't really a searchable value. Also
 * toggles the "no matches" placeholder so an empty result doesn't just look like a stuck
 * loading state.
 */
function filterCategoryOptions(wrapper: HTMLElement, query: string): void {
  const needle = query.trim().toLowerCase();
  const options = Array.from(wrapper.querySelectorAll<HTMLElement>(".file-category-option"));
  let anyVisible = false;
  options.forEach(option => {
    const isPlaceholder = option.dataset.value === "";
    const haystack = `${option.textContent ?? ""} ${option.dataset.value ?? ""}`.toLowerCase();
    const visible = isPlaceholder || !needle || haystack.includes(needle);
    option.style.display = visible ? "" : "none";
    if (visible && !isPlaceholder) anyVisible = true;
  });

  const noMatch = wrapper.querySelector<HTMLElement>(".file-category-option--no-match");
  if (noMatch) noMatch.style.display = needle && !anyVisible ? "block" : "none";
}

/** Closes wrapper's menu and, if the field's text doesn't match its last committed
 * selection (i.e. the user typed a filter query but never picked anything), reverts it. */
function closeCategoryField(wrapper: HTMLElement): void {
  wrapper.classList.remove("file-category--open");
  const field = wrapper.querySelector<HTMLInputElement>(".file-category-button");
  if (!field) return;
  field.setAttribute("aria-expanded", "false");
  field.value = field.dataset.selectedText ?? "";
}

/**
 * Commits option as wrapper's Document Type: stores it, updates the field's displayed
 * text/committed dataset/required-highlight, closes the menu, and (for a real selection,
 * not clearing back to blank) auto-checks the row -- mirrors the same "picking a document
 * type signals intent to upload this file" auto-check as before.
 */
function commitCategorySelection(wrapper: HTMLElement, option: HTMLElement): void {
  const id = wrapper.dataset.id;
  const field = wrapper.querySelector<HTMLInputElement>(".file-category-button");
  if (!id || !field) return;

  const value = option.dataset.value ?? "";
  const text = option.dataset.text ?? "";

  setDocType(id, value);

  field.value = text;
  field.dataset.selectedValue = value;
  field.dataset.selectedText = text;
  field.classList.toggle("file-category-button--empty", value === "");
  field.classList.toggle("file-category-button--required", option.classList.contains("file-category-option--required"));
  field.classList.remove("file-category-button--error");

  closeCategoryField(wrapper);
  clearMapUploadStatus();

  // Picking an actual document type signals intent to upload this file -- auto-check it
  // (if it wasn't already) so the user doesn't also have to tick the checkbox by hand.
  // Clearing back to "Document Type" (value === "") doesn't imply that, so it leaves the
  // checked state alone.
  if (value) {
    selectedFiles[id] = true;
    const row = wrapper.closest<HTMLElement>(".file-row");
    const checkbox = row?.querySelector<HTMLInputElement>(".file-checkbox");
    if (checkbox) checkbox.checked = true;
    row?.classList.add("file-row--checked");
    updateMapUploadButtonState();
  }
}

function queueFiles(files: FileList | File[]): void {
  if (!files.length) return;
  addLocalFiles(files);
  renderAttachmentsCollection();
}

function toDroppedOfficeFile(item: DroppedItemDetailsExt): File | null {
  if (!item?.fileContent) return null;

  const fileName = String(item.name ?? "").trim() || "dropped-item";
  const contentType = String(item.type ?? "").trim() || item.fileContent.type || "application/octet-stream";
  return new File([item.fileContent], fileName, { type: contentType, lastModified: 0 });
}

// ─── Attachments-degraded banner ────────────

let attachmentsDegradedBannerInitialized = false;

/**
 * Non-sticky, like the order-search-status banner in orders.ts: re-evaluated on every
 * render, so dismissing it hides it only until the next refresh finds the same (or a new)
 * degraded email -- it never permanently hides a real, ongoing problem.
 */
function updateAttachmentsDegradedBanner(): void {
  const message = hasDegradedEmailSelection()
    ? "Some attachments may not have loaded correctly. Click Retry; if it keeps happening, restart Outlook."
    : "";
  setStatusBanner("attachments-degraded-status", "attachments-degraded-status-text", message, "error");
}

/** Wires the banner's Reload/dismiss buttons once. Safe to call multiple times. */
export function initAttachmentsDegradedBanner(): void {
  if (attachmentsDegradedBannerInitialized) return;
  attachmentsDegradedBannerInitialized = true;

  document.getElementById("attachments-degraded-status-refresh")?.addEventListener("click", () => {
    // Deliberately NOT window.location.reload(): that was tried, and in classic Outlook it
    // didn't clear this -- users still needed to restart Outlook itself, so whatever's stuck
    // lives in the host, not in this page's JS state. A plain re-sync is non-destructive
    // (a reload would also drop unsaved document types/comments) and does help when the
    // failure was transient.
    requestSelectionRefresh();
  });
  document.getElementById("attachments-degraded-status-close")?.addEventListener("click", () => {
    setStatusBanner("attachments-degraded-status", "attachments-degraded-status-text", "");
  });
}

// ─── Render ─────────────────────────────────

export function renderAttachmentsCollection(): void {

  updateAttachmentsDegradedBanner();

  const container = document.getElementById("attachments-list");
  if (!container) return;

  if (collectedEmails.length === 0 && localFiles.length === 0) {
    container.innerHTML = `<div class="attachment-empty">No files yet. Select email(s) or drop local files.</div>`;
    updateMapUploadButtonState();
    return;
  }

  const sections: string[] = [];
  const allEntries: FileEntry[] = [];

  collectedEmails.forEach((email) => {
    const entries: FileEntry[] = [
      {
        id: getEmailFileId(email.key),
        name: normalizeEmlFileName(email.subject || "email"),
        size: null,
        isLocal: false,
        sourceEmailKey: email.key,
      },
      ...email.attachments.map((a) => ({
        id: getAttachmentFileId(email.key, a.id),
        name: a.name,
        size: (a as AttachmentDetailsExt).size ?? null,
        isLocal: false,
        sourceEmailKey: email.key,
      })),
    ];
    allEntries.push(...entries);

    const rows = entries.map((f) => renderFileRow(f, DocumentTypes)).join("");
    sections.push(`
      <div class="file-group" data-email-group="${escapeHtml(email.key)}">
        <div class="file-group-rows file-group-rows--email">${rows}</div>
      </div>
    `);
  });

  if (localFiles.length > 0) {
    const localEntries: FileEntry[] = localFiles.map((f) => ({
      id: f.id,
      name: f.file.name,
      size: f.file.size ?? null,
      isLocal: true,
    }));
    allEntries.push(...localEntries);

    const rows = localEntries.map((f) => renderFileRow(f, DocumentTypes)).join("");
    sections.push(`
      <div class="file-group">
        <div class="file-group-title">
          <span>Local files</span>
        </div>
        <div class="file-group-rows file-group-rows--local">${rows}</div>
      </div>
    `);
  }

  container.innerHTML = sections.join("");
  wireFileRowEvents(container);
  clearMapUploadStatus();
  updateMapUploadButtonState();
  void refreshUploadedBadges(allEntries);
}

function renderFileRow(f: FileEntry, docTypeOptions: DocType[]): string {
  const nameKey = normalizeFileNameForCompare(f.name);
  const isUploaded = isFileNameUploaded(nameKey);
  const checked = isFileChecked(f.id, isUploaded);
  const icon = getFileIcon(getExtension(f.name));
  const sizeLabel = f.size !== null ? `<span class="file-size">${formatSize(f.size)}</span>` : "";
  // Sits under the checkbox/icon in file-select-col, beside the Document Type/Comment
  // fields in file-properties (see the row layout below) -- not inline with the name/size,
  // so a badge appearing/disappearing doesn't reflow the name line. See applyUploadedBadge
  // for the matching update when the "already uploaded" check resolves after the initial
  // render.
  const uploadedBadge = isUploaded ? `<span class="file-select-uploaded">Uploaded</span>` : "";
  const removeLocalBtn = f.isLocal
    ? `<button type="button" class="file-remove-btn" data-remove-id="${escapeHtml(f.id)}" title="Remove local file" aria-label="Remove local file">&#10005;</button>`
    : "";

  const details = getOrCreateAttachmentDetails(f.id);

  const selectedDocType = docTypeOptions.find(x => x.code === details.docType);
  const selectedText = selectedDocType?.description ?? "";
  const isRequired = Boolean(selectedDocType?.emailBridgeRequired);

  // The field itself is the search box -- typing directly into it filters the menu below
  // (see wireFileRowEvents), rather than requiring a separate search input inside the
  // dropdown. data-selected-value/-text are the last *committed* selection (only a click or
  // Enter on an option changes them); blur/Escape revert the field's displayed text back to
  // them, so a filter query left half-typed never gets mistaken for an actual choice.
  const categoryDropdown = `
  <div class="file-category" data-id="${escapeHtml(f.id)}">
    <input type="text"
        class="file-category-button ${!details.docType ? "file-category-button--empty" : ""} ${isRequired ? "file-category-button--required" : ""}"
        value="${escapeHtml(selectedText)}"
        data-selected-value="${escapeHtml(details.docType ?? "")}"
        data-selected-text="${escapeHtml(selectedText)}"
        placeholder="Document Type"
        autocomplete="off"
        spellcheck="false"
        role="combobox"
        aria-expanded="false"
        aria-autocomplete="list" />

      <div class="file-category-menu">
          <div class="file-category-options">
              <div class="file-category-option" data-value="" data-text="">
                  Document Type
              </div>

              ${docTypeOptions.map(o => `
                  <div class="file-category-option ${o.emailBridgeRequired ? "file-category-option--required" : ""}"
                      data-value="${escapeHtml(o.code)}"
                      data-text="${escapeHtml(o.description)}">
                      ${escapeHtml(o.description)}
                  </div>
              `).join("")}
              <div class="file-category-option--no-match">No matches</div>
          </div>
      </div>
  </div>`

  const noteInput = `
  <input
      type="text" class="file-note" data-id="${escapeHtml(f.id)}"
      value="${escapeHtml(details.comment ?? "")}" placeholder="Comment" />`;

  // A small chip, not a checkbox -- with the row's own selection checkbox right there too,
  // a checkbox-shaped control here reads as "select this file" at a glance. Sits in
  // file-actions-row, under Comment (see file-properties), alongside the (currently inert)
  // "Multiple Docs. Req." chip -- text-only content, so unlike the switch this replaced it
  // has no track/knob graphic to fit.
  const portalToggle = `
  <button type="button"
      class="file-portal-toggle ${details.allowSynchToPortal ? "file-portal-toggle--active" : ""}"
      data-id="${escapeHtml(f.id)}"
      aria-pressed="${details.allowSynchToPortal ? "true" : "false"}"
      title="Allow this file to sync to the portal">
      Sync to Portal
  </button>`;

  // Placeholder only -- not wired to any logic yet (deliberately a distinct class from
  // file-portal-toggle, which wireFileRowEvents binds a click handler to). Hidden for now
  // (visibility, not display -- see .file-multidocs-chip--hidden) until that logic exists,
  // but still occupies its half of file-actions-row so Portal's width/position don't shift
  // to fill the row now that it's the only visible chip. tabindex/aria-hidden keep an
  // invisible button out of the tab order and off screen readers.
  const multipleDocsChip = `
  <button type="button" class="file-multidocs-chip file-multidocs-chip--hidden" tabindex="-1" aria-hidden="true" title="Multiple Docs. Req.">
      Multiple Docs. Req.
  </button>`;

  return `
    <div class="file-row${checked ? " file-row--checked" : ""}${isUploaded ? " file-row--uploaded" : ""}" data-id="${escapeHtml(f.id)}" data-name-key="${escapeHtml(nameKey)}">
      <div class="file-name-row">
        <span class="file-name" title="${escapeHtml(f.name)}">${escapeHtml(f.name)}</span>
        ${sizeLabel}
      </div>
      <div class="file-body">
        <div class="file-select-col">
          <div class="file-select-top">
            <input type="checkbox" class="file-checkbox" data-id="${escapeHtml(f.id)}" ${checked ? "checked" : ""}/>
            <span class="file-icon">${icon}</span>
          </div>
          ${uploadedBadge}
        </div>
        <div class="file-properties">
          ${categoryDropdown}
          ${noteInput}
          <div class="file-actions-row">
            ${portalToggle}
            ${multipleDocsChip}
          </div>
        </div>
      </div>
      ${removeLocalBtn}
    </div>`;
}

// ─── Live "already uploaded" check ──────────

/**
 * Checks each distinct file name currently in the list against BC's ord_linkedDocuments_v2
 * (scoped to the single auto-selected order, if there is exactly one -- see
 * getSingleAutoSelectedOrderNo -- otherwise any order) and patches the "uploaded" badge on
 * matching rows directly, without a full re-render (so it doesn't disturb unrelated UI
 * state like an open dropdown or a comment field mid-edit). One request per distinct name,
 * in parallel. Purely informational -- see renderFileRow / prepareUploadCandidates in
 * customers.ts: an "uploaded" file is never hidden, disabled, or excluded from re-upload.
 */
async function refreshUploadedBadges(entries: FileEntry[]): Promise<void> {
  const byNameKey = new Map<string, string>(); // normalized -> original name, for the query
  entries.forEach((e) => {
    const key = normalizeFileNameForCompare(e.name);
    if (key && !byNameKey.has(key)) byNameKey.set(key, e.name);
  });
  if (byNameKey.size === 0) return;

  let bc: BcRequestContext;
  try {
    bc = await getBcRequestContext(false);
  } catch {
    return; // not signed in -- skip silently, this is a background convenience
  }

  const orderNo = getSingleAutoSelectedOrderNo();

  await Promise.all(
    Array.from(byNameKey.entries()).map(async ([nameKey, originalName]) => {
      let uploaded: boolean;
      try {
        uploaded = await isFileAlreadyLinked(originalName, orderNo, bc);
      } catch (err: unknown) {
        console.error(`Failed to check upload status for "${originalName}":`, getErrorMessage(err));
        return;
      }
      applyUploadedBadge(nameKey, uploaded);
    }),
  );
}

function applyUploadedBadge(nameKey: string, uploaded: boolean): void {
  setFileNameUploaded(nameKey, uploaded);

  const container = document.getElementById("attachments-list");
  if (!container) return;

  container.querySelectorAll<HTMLElement>(".file-row").forEach((row) => {
    if (row.dataset.nameKey !== nameKey) return;
    row.classList.toggle("file-row--uploaded", uploaded);

    // Mirrors renderFileRow's checked formula (isFileChecked) so a badge discovered after
    // the initial render still ends up showing the same checked state a fresh render would
    // have produced -- and never clobbers a manual check/uncheck the user already made.
    const id = row.dataset.id;
    const checked = id ? isFileChecked(id, uploaded) : !uploaded;
    row.classList.toggle("file-row--checked", checked);
    const checkbox = row.querySelector<HTMLInputElement>(".file-checkbox");
    if (checkbox) checkbox.checked = checked;

    // Lives in file-select-col (under the checkbox/icon), not inline with the name/size --
    // see renderFileRow for why. Appended as selectCol's last child so a badge discovered
    // here (after the initial render) ends up in the same position a fresh render would
    // have put it.
    const selectCol = row.querySelector(".file-select-col");
    if (selectCol) {
      const existingBadge = selectCol.querySelector(".file-select-uploaded");
      if (uploaded && !existingBadge) {
        const badgeHtml = `<span class="file-select-uploaded">Uploaded</span>`;
        selectCol.insertAdjacentHTML("beforeend", badgeHtml);
        // Adding the badge grows this row, shifting everything below it -- an already-open
        // dropdown menu is `position: fixed` at coordinates computed when it opened, so it
        // wouldn't follow that shift and would end up overlapping whatever moved under it.
        // Closing it (same as the existing scroll/resize handling) is simpler and safer
        // than trying to reposition it.
        closeAllCategoryMenus();
      } else if (!uploaded && existingBadge) {
        existingBadge.remove();
        closeAllCategoryMenus();
      }
    }
  });
}

function wireFileRowEvents(container: HTMLElement): void {
  container.querySelectorAll<HTMLInputElement>(".file-checkbox").forEach((cb) => {
    cb.addEventListener("change", () => {
      const id = cb.dataset.id!;
      selectedFiles[id] = cb.checked;
      const row = Array.from(container.querySelectorAll<HTMLElement>(".file-row")).find(
        (item) => item.dataset.id === id,
      );
      row?.classList.toggle("file-row--checked", cb.checked);
      updateMapUploadButtonState();
    });
  });

  // Mirrors the click-to-toggle row behavior on .bc-record in orders.ts.
  container.querySelectorAll<HTMLElement>(".file-row").forEach((row) => {
    row.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;

      // A click landing on the checkbox itself already toggles it (and fires the "change"
      // listener above) -- handling it again here would just flip it right back.
      if (target.closest(".file-checkbox")) return;

      // Document Type, Comment, and the Portal/Multiple Docs. Req. chips all live inside
      // file-properties -- clicking any of them (typing, opening the dropdown, toggling
      // Portal, selecting text) should work normally, not also toggle the row's checkbox.
      // file-remove-btn (outside file-properties) doesn't need its own exclusion here: its
      // click handler already calls stopPropagation, so a click there never reaches this
      // listener at all.
      if (target.closest(".file-properties")) return;

      // Don't hijack a click-drag used to select the file name for copying -- only a plain
      // click (nothing selected) toggles the row.
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) return;

      // .click() (rather than flipping .checked directly) fires the checkbox's own "change"
      // listener too, so selection state stays in one place.
      row.querySelector<HTMLInputElement>(".file-checkbox")?.click();
    });
  });

  container.querySelectorAll<HTMLButtonElement>(".file-portal-toggle").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const id = btn.dataset.id!;
      const next = !btn.classList.contains("file-portal-toggle--active");
      btn.classList.toggle("file-portal-toggle--active", next);
      btn.setAttribute("aria-pressed", String(next));
      setAllowSynchToPortal(id, next);
    });
  });

  container.querySelectorAll<HTMLButtonElement>(".file-remove-btn").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const id = btn.dataset.removeId;
      if (!id) return;
      removeLocalFile(id);
      renderAttachmentsCollection();
    });
  });

  // The Document Type field is itself the search box (a text input, not a button) -- see
  // renderFileRow. Opening happens on focus; typing filters in place; a click or Enter on an
  // option commits it; blur/Escape without a commit revert the field back to whatever was
  // last actually selected (data-selected-value/-text), so a half-typed filter query is
  // never mistaken for a real choice.
  container.querySelectorAll<HTMLInputElement>(".file-category-button").forEach((field) => {
      const openMenu = () => {
          const wrapper = field.closest<HTMLElement>(".file-category");
          if (!wrapper) return;

          container
              .querySelectorAll(".file-category")
              .forEach(item => {
                  if (item !== wrapper) closeCategoryField(item as HTMLElement);
              });

          wrapper.classList.add("file-category--open");
          field.setAttribute("aria-expanded", "true");
          positionCategoryMenu(wrapper, field);
          filterCategoryOptions(wrapper, "");
          field.select();
      };

      field.addEventListener("focus", openMenu);
      // A click when already focused (menu already open) wouldn't otherwise refire focus.
      field.addEventListener("click", (e) => {
          e.stopPropagation();
          const wrapper = field.closest<HTMLElement>(".file-category");
          if (wrapper && !wrapper.classList.contains("file-category--open")) openMenu();
      });
      field.addEventListener("mousedown", e => e.stopPropagation());

      field.addEventListener("input", () => {
          const wrapper = field.closest<HTMLElement>(".file-category");
          if (wrapper) filterCategoryOptions(wrapper, field.value);
      });

      field.addEventListener("keydown", e => {
          const wrapper = field.closest<HTMLElement>(".file-category");
          if (!wrapper) return;

          if (e.key === "Escape") {
              e.stopPropagation();
              closeCategoryField(wrapper);
              return;
          }

          if (e.key === "Enter") {
              e.preventDefault();
              // Picks the first visible, real (non-placeholder) match -- lets typing a
              // distinctive-enough prefix and hitting Enter select without ever touching
              // the mouse.
              const firstMatch = Array.from(wrapper.querySelectorAll<HTMLElement>(".file-category-option"))
                  .find(o => o.dataset.value !== "" && o.style.display !== "none");
              if (firstMatch) commitCategorySelection(wrapper, firstMatch);
          }
      });

      field.addEventListener("blur", () => {
          const wrapper = field.closest<HTMLElement>(".file-category");
          if (wrapper) closeCategoryField(wrapper);
      });
  });

  container.querySelectorAll<HTMLElement>(".file-category-option").forEach(option => {
    // mousedown (not click) is where this matters: preventDefault here stops the field from
    // ever blurring in the first place, so selecting an option never races against (or gets
    // treated as) the blur-reverts-uncommitted-text handling above.
    option.addEventListener("mousedown", e => e.preventDefault());
    option.addEventListener("click", e => {
        e.preventDefault();
        e.stopPropagation();

        const wrapper = option.closest<HTMLElement>(".file-category");
        if (wrapper) commitCategorySelection(wrapper, option);
    });
  });

  container.querySelectorAll<HTMLInputElement>(".file-note").forEach(note => {
    note.addEventListener("input", () => {
        const id = note.dataset.id!;
        setComment(id, note.value);
    });
  });
}

// ─── File dropzone ──────────────────────────

export function initFileDropzone(): void {
  const dropzone = document.getElementById("file-dropzone");
  const input = document.getElementById("input-local-files") as HTMLInputElement | null;
  const button = document.getElementById("btn-add-local-files");

  if (!dropzone || !input || !button) return;

  const preventDefaults = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
  };

  ["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
    dropzone.addEventListener(eventName, preventDefaults);
  });

  dropzone.addEventListener("dragenter", () => dropzone.classList.add("file-dropzone--active"));
  dropzone.addEventListener("dragover", () => dropzone.classList.add("file-dropzone--active"));
  dropzone.addEventListener("dragleave", () => dropzone.classList.remove("file-dropzone--active"));
  dropzone.addEventListener("drop", (e: DragEvent) => {
    dropzone.classList.remove("file-dropzone--active");
    if (!e.dataTransfer?.files?.length) return;
    queueFiles(e.dataTransfer.files);
  });

  button.addEventListener("click", () => input.click());
  input.addEventListener("change", () => {
    if (!input.files?.length) return;
    queueFiles(input.files);
    input.value = "";
  });
}

export function initMailboxDropzone(): void {
  const dropzone = document.getElementById("file-dropzone");
  if (!dropzone) return;
  if (!Office.context.requirements.isSetSupported("Mailbox", "1.5")) return;

  const eventType = (((Office.EventType as unknown as { DragAndDropEvent?: string }).DragAndDropEvent ??
    "olkDragAndDropEvent") as unknown) as Office.EventType;

  Office.context.mailbox.addHandlerAsync(
    eventType,
    (event: unknown) => {
      const args = event as unknown as DragAndDropEventArgsExt;
      const data = args?.dragAndDropEventData;
      if (!data) return;

      if (data.type === "dragover") {
        dropzone.classList.add("file-dropzone--active");
        return;
      }

      dropzone.classList.remove("file-dropzone--active");
      const officeFiles = data.dataTransfer?.files ?? [];
      if (!officeFiles.length) return;

      const files = officeFiles.map(toDroppedOfficeFile).filter((file): file is File => file !== null);
      if (!files.length) return;

      queueFiles(files);
    },
    (result: Office.AsyncResult<void>) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        console.warn("Unable to register mailbox drag-and-drop handler:", result.error?.message);
      }
    },
  );
}
